import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

// Define the content for the multi-step text module
const moduleContent: ModuleSection[] = [
  {
    title: 'Why Gratitude Is Essential for Your Well-being',
    content: [
      'Practicing gratitude isn\'t just about feeling good; it\'s about actively rewiring your brain and healing your emotions.',    
    ],
    additionalSection: {
      title: 'Reframe Challenges and Heal',
      content: [
        'When a difficulty arises, it\'s easy to fall into despair or victimhood. Gratitude offers the way out by forcing you to reframe the challenge as an opportunity for growth. For example, instead of just feeling the pain of a loss, you can be grateful for the strength you gained or the lessons you learned because of it. This ability to see lessons in hardship is crucial for emotional healing. It allows you to integrate difficult experiences rather than being defined by them.'
      ]
    },
    additionalSection2: {
      title: 'Healing the Inner Critic',
      content: [
        'Your mind naturally focuses on threats and problems. Gratitude actively counteracts this negative bias. By intentionally listing or focusing on the good things, you build appreciation habits. This practice trains your mind to look for light, which naturally fosters optimism. The more you look for good, the more good you find, creating a positive spiritual feedback loop that becomes the "heartbeat of spiritual joy."',
      ]
    },
  },
  {
    title: 'Simple Tools to Cultivate Thankfulness',
    content: [
      'These practices are designed to integrate thankfulness into your daily life without taking up a lot of time.',
      '• <strong>Gratitude Journal:</strong> Commit to writing down just three things you\'re genuinely thankful for each night. They don\'t have to be big things–the comfort of a warm bed, a kind text, or a good meal are perfect. This simple ritual ensures your day ends with a focus on what went right, not what went wrong.',
      '• <strong>Gratitude Walk:</strong> The next time you walk outdoors, consciously use the time to reflect on your blessings. Notice the beauty in simplicity–the sky, the trees, the ground supporting you. This links your appreciation to your physical reality and your connection to nature.',
      '• <strong>Thankfulness Sharing:</strong> Make a point to verbally express gratitude to one person each day. A thank-you note, a sincere compliment, or a simple "I appreciate you" helps solidify the feeling in your own heart while strengthening your connections with others.',
      '<strong>Key Message:</strong> Gratitude is not a passive emotion; it\'s an active choice that shifts your emotional state and opens your heart to the richness of life.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about gratitude and seeing life as a gift.',
        'The quiz will help you review key concepts about practicing thankfulness, reframing challenges, and cultivating appreciation in daily life.'
      ]
    }
  },
];
// Main component, matching the design of the standard assessment form
const SpiritualModule5Page = () => {
  const navigate = useNavigate();
  const { currentStep: rawCurrentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM5');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within valid bounds
  const currentStep = Math.min(rawCurrentStep, totalSteps - 1);

  const currentModule = moduleContent[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep);
    } else {
      // Mark the module as completed and navigate to quiz
      await saveProgress(currentStep, true);
      navigate('/panel/learn/spiritual-module-5/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/spiritual-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep);
  };

  const isCompleted = currentStep === totalSteps - 1;

  return (
    // 1. Updated wrapper class: module-form-page -> module-form
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        {/* 2. Updated left panel class: module-left-panel -> module-left */}
        <div 
          className="module-left spiritual-panel"
        >
          {/* 3. Retaining module-header for left panel content */}
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Gratitude – Seeing Life as a Gift</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
            Gratitude is a spiritual powerhouse because it's a transformer of perception. It moves far beyond just being polite and saying "thank you." It is a deep, internal decision to recognize the inherent value of your life, even when things are messy or painful.
            </p>
            <p className="module-tool-info">
            A truly grateful person sees life not as something owed to them, but as a continuous gift. This simple shift is the key to spiritual joy because it changes the fundamental question your mind asks: it shifts you from obsessing over "what's missing" to appreciating "what's meaningful."
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        {/* 4. Updated right panel class: module-right-panel -> module-right */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content" style={{ paddingBottom: '80px' }}>
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {` ${currentModule.title}`}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {/* Additional Title Sections */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              // Logic moved to handleBack, so button is never disabled until the end state is reached.
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Back'}
            </button>
            
            <button
              className="btn-primary"
              onClick={handleNext}
            >
              {isCompleted ? 'Take the Quiz' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule5Page;

